import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmtInt, PieChart } from './_finHelpers';

// Debt-to-Income (DTI) Ratio Calculator — mirrors reference image exactly
export default function DebtToIncomeCalc() {
  const [incomes, setIncomes] = useState({
    salary:     { val: 60000, freq: 'year' },
    pension:    { val: 0, freq: 'year' },
    investment: { val: 0, freq: 'year' },
    other:      { val: 0, freq: 'year' },
  });
  const [debts, setDebts] = useState({
    rent:        { val: 1200, freq: 'month' },
    mortgage:    { val: 0,    freq: 'month' },
    propTax:     { val: 0,    freq: 'year'  },
    hoa:         { val: 0,    freq: 'month' },
    homeIns:     { val: 0,    freq: 'year'  },
    creditCards: { val: 200,  freq: 'month' },
    studentLoan: { val: 0,    freq: 'month' },
    autoLoan:    { val: 250,  freq: 'month' },
    otherLoans:  { val: 0,    freq: 'month' },
  });
  const toMonthly = (v, f) => (f === 'year' ? v / 12 : v);
  const c = useMemo(() => {
    const monthlyIncome = Object.values(incomes).reduce((a, b) => a + toMonthly(b.val, b.freq), 0);
    const housing = ['rent','mortgage','propTax','hoa','homeIns'].reduce((a,k)=>a+toMonthly(debts[k].val, debts[k].freq),0);
    const otherDebt = ['creditCards','studentLoan','autoLoan','otherLoans'].reduce((a,k)=>a+toMonthly(debts[k].val, debts[k].freq),0);
    const monthlyDebt = housing + otherDebt;
    const backDti = monthlyIncome > 0 ? (monthlyDebt / monthlyIncome) * 100 : 0;
    const frontDti = monthlyIncome > 0 ? (housing / monthlyIncome) * 100 : 0;
    const remaining = Math.max(0, monthlyIncome - monthlyDebt);
    // House affordability after halting rent/mortgage: use 36% of income limit, minus non-housing debt
    const newHouseMax = Math.max(0, monthlyIncome * 0.36 - otherDebt);
    // Estimated home value: 30-year @ 6.699%, 20% down, 2% PITI overhead
    const rate = 0.06699 / 12;
    const n = 360;
    const piti = newHouseMax * 0.85; // ~85% goes to P&I, ~15% to tax+insurance
    const loan = (piti * (Math.pow(1 + rate, n) - 1)) / (rate * Math.pow(1 + rate, n));
    const homeValue = loan / 0.80; // 20% down
    return { monthlyIncome, monthlyDebt, housing, otherDebt, remaining, backDti, frontDti, newHouseMax, homeValue };
  }, [incomes, debts]);

  const verdict = c.backDti < 35 ? 'good' : c.backDti < 50 ? 'stressful' : 'very stressful';
  const verdictColor = c.backDti < 35 ? '#16a34a' : c.backDti < 50 ? '#f59e0b' : '#dc2626';

  const setInc = (k, patch) => setIncomes(prev => ({ ...prev, [k]: { ...prev[k], ...patch } }));
  const setDbt = (k, patch) => setDebts(prev => ({ ...prev, [k]: { ...prev[k], ...patch } }));

  const IncomeRow = ({ k, label, hint }) => (
    <div className="mb-3">
      <label className="block text-sm text-slate-700 mb-1">{label}</label>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex border border-slate-300 rounded overflow-hidden">
          <span className="px-2 bg-slate-100 border-r border-slate-300 flex items-center text-slate-500">$</span>
          <input type="number" value={incomes[k].val} onChange={e=>setInc(k, {val:+e.target.value||0})} className="flex-1 px-2 py-1 outline-none"/>
        </div>
        <select value={incomes[k].freq} onChange={e=>setInc(k, {freq:e.target.value})} className="border border-slate-300 rounded px-2 py-1">
          <option value="year">/ year</option>
          <option value="month">/ month</option>
        </select>
      </div>
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
  const DebtRow = ({ k, label, hint }) => (
    <div className="mb-3">
      <label className="block text-sm text-slate-700 mb-1">{label}</label>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex border border-slate-300 rounded overflow-hidden">
          <span className="px-2 bg-slate-100 border-r border-slate-300 flex items-center text-slate-500">$</span>
          <input type="number" value={debts[k].val} onChange={e=>setDbt(k, {val:+e.target.value||0})} className="flex-1 px-2 py-1 outline-none"/>
        </div>
        <select value={debts[k].freq} onChange={e=>setDbt(k, {freq:e.target.value})} className="border border-slate-300 rounded px-2 py-1">
          <option value="year">/ year</option>
          <option value="month">/ month</option>
        </select>
      </div>
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );

  return (
    <CalcShell look="cnet" title="Debt-to-Income (DTI) Ratio Calculator" subtitle="Calculate your front-end and back-end DTI ratios — key metrics used by lenders."
      about={<>
        <p>The <b>DTI ratio</b> compares monthly debt payments to gross monthly income. The <b>front-end ratio</b> counts only housing costs; the <b>back-end ratio</b> counts all recurring debt. Conventional lenders typically look for a back-end DTI of 36% or less; FHA allows up to 43%.</p>
      </>}
      slug="debt-to-income"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h3 className="font-bold text-slate-900 mb-3">Incomes (Before Tax)</h3>
          <IncomeRow k="salary"     label="Salary & Earned Income" />
          <IncomeRow k="pension"    label="Pension & Social Security" />
          <IncomeRow k="investment" label="Investment & Savings" hint="interest, capital gain, dividend, rental income..." />
          <IncomeRow k="other"      label="Other Income" hint="gift, alimony, child support..." />
        </div>
        <div>
          <h3 className="font-bold text-slate-900 mb-3">Debts / Expenses</h3>
          <DebtRow k="rent"        label="Rental Cost" />
          <DebtRow k="mortgage"    label="Mortgage" />
          <DebtRow k="propTax"     label="Property Tax" />
          <DebtRow k="hoa"         label="HOA Fees" />
          <DebtRow k="homeIns"     label="Homeowner Insurance" />
          <DebtRow k="creditCards" label="Credit Cards" />
          <DebtRow k="studentLoan" label="Student Loan" />
          <DebtRow k="autoLoan"    label="Auto Loan" />
          <DebtRow k="otherLoans"  label="Other Loans and Liabilities" hint="personal loan, child support, alimony, etc." />
        </div>
      </div>

      {/* Results panel — matches image */}
      <div className="mt-8 rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-emerald-700 text-white px-4 py-2 font-bold flex items-center justify-between">
          <span>Results</span>
          <span className="text-xs opacity-80">save</span>
        </div>
        <div className="p-5">
          <h3 className="text-2xl font-semibold text-slate-900">
            Debt-to-Income (DTI) Ratio: <span className="text-emerald-700" data-testid="dti-back">{c.backDti.toFixed(0)}%</span>.
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 items-center">
            {/* Gauge */}
            <div>
              <DTIGauge value={c.backDti} />
              <div className="text-slate-700 mt-3 text-lg" style={{color:verdictColor}}>Your DTI ratio is <b>{verdict}</b>.</div>
              <table className="w-full text-sm mt-4 border border-slate-200">
                <tbody>
                  <tr className="bg-slate-50"><td className="px-3 py-1.5 border">Back-End DTI Ratio:</td><td className="px-3 py-1.5 border">{c.backDti.toFixed(0)}%</td></tr>
                  <tr><td className="px-3 py-1.5 border">Front-End DTI Ratio:</td><td className="px-3 py-1.5 border" data-testid="dti-front">{c.frontDti.toFixed(0)}%</td></tr>
                  <tr className="bg-slate-50"><td className="px-3 py-1.5 border">Total Income:</td><td className="px-3 py-1.5 border">{fmtInt(c.monthlyIncome*12)} / year or {fmtInt(c.monthlyIncome)} / month</td></tr>
                  <tr><td className="px-3 py-1.5 border">Total Debt:</td><td className="px-3 py-1.5 border">{fmtInt(c.monthlyDebt*12)} / year or {fmtInt(c.monthlyDebt)} / month</td></tr>
                </tbody>
              </table>
            </div>
            {/* Income Breakdown donut */}
            <div>
              <h4 className="text-lg font-bold text-slate-900 mb-2 text-center">Income Breakdown</h4>
              <PieChart parts={[
                { label: 'House Debts/Expenses', value: c.housing,    color: '#3b82f6' },
                { label: 'Other Debts/Expenses', value: c.otherDebt,  color: '#22c55e' },
                { label: 'Remainings',           value: c.remaining,  color: '#8b0000' },
              ]} />
            </div>
          </div>

          <p className="text-sm text-slate-700 mt-6">
            If you live in the U.S. and have plans to purchase a house after halted payments towards preexisting rent and mortgage,
            you can spend up to <b>{fmtInt(c.newHouseMax)}</b> per month on the new house, which is equivalent to a house valued up to <b>{fmtInt(c.homeValue)}</b>.*
          </p>
          <p className="text-xs text-slate-500 mt-1">
            * The calculation is based on the assumption of using 30 years conventional loans with 6.699% interest rate containing a 20% down payment and estimation of 2% spent on property tax and insurance.
          </p>
        </div>
      </div>

      <StepsPanel steps={[
        `Monthly income = ${fmtInt(c.monthlyIncome)} (normalised to monthly)`,
        `Front-end DTI = housing ${fmtInt(c.housing)} / income = <b>${c.frontDti.toFixed(2)}%</b>`,
        `Back-end DTI = all debt ${fmtInt(c.monthlyDebt)} / income = <b>${c.backDti.toFixed(2)}%</b>`,
      ]} />
    </CalcShell>
  );
}

function DTIGauge({ value }) {
  // Bar spans 0-60%. Zones: green 0-35, yellow 35-50, red 50-60
  const max = 60;
  const clamp = Math.max(0, Math.min(max, value));
  const pos = (clamp / max) * 100;
  const p35 = (35 / max) * 100;
  const p50 = (50 / max) * 100;
  return (
    <div className="relative w-full pt-8">
      <div className="absolute -top-1 text-sm font-bold text-slate-900" style={{ left: `calc(${pos}% - 20px)` }}>
        {value.toFixed(0)}%
        <div className="text-center leading-none">▼</div>
      </div>
      <div className="relative h-6 rounded overflow-hidden flex text-white text-xs font-semibold">
        <div className="flex items-center justify-center bg-emerald-600" style={{ width: `${p35}%` }}>Safe</div>
        <div className="flex items-center justify-center bg-yellow-400 text-slate-900" style={{ width: `${p50 - p35}%` }}></div>
        <div className="flex items-center justify-center bg-red-600" style={{ width: `${100 - p50}%` }}>Very Stressful</div>
      </div>
      <div className="relative text-xs text-slate-600 mt-1">
        <span style={{ position: 'absolute', left: `calc(${p35}% - 14px)` }}>35%</span>
        <span style={{ position: 'absolute', left: `calc(${p50}% - 14px)` }}>50%</span>
      </div>
    </div>
  );
}
